// ======================================
// Admin Pricing Engine Module
// ======================================

import { apiRequest } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderPricingAdmin() {
  const content = `
      <div class="admin-page-header">
        <div>
          <h2 class="admin-page-title" style="margin:0;">Pricing Engine</h2>
          <p class="admin-page-subtitle">Manage city-based pricing rates and add-ons</p>
        </div>
        <button class="btn btn-primary" id="add-rate-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Rate
        </button>
      </div>

      <!-- City Filter -->
      <div class="admin-filters" style="display:flex;gap:var(--space-4);margin-bottom:var(--space-6);flex-wrap:wrap;">
        <select id="city-filter" class="form-input" style="max-width:200px;">
          <option value="">All Cities</option>
          <option value="Chennai">Chennai</option>
          <option value="Bangalore">Bangalore</option>
          <option value="Coimbatore">Coimbatore</option>
          <option value="Hyderabad">Hyderabad</option>
        </select>
        <select id="category-filter" class="form-input" style="max-width:200px;">
          <option value="">All Categories</option>
          <option value="modular_kitchen">Modular Kitchen</option>
          <option value="wardrobe">Wardrobe</option>
          <option value="flooring">Flooring</option>
          <option value="painting">Painting</option>
          <option value="false_ceiling">False Ceiling</option>
          <option value="electrical">Electrical</option>
        </select>
      </div>

      <!-- Rates Table -->
      <div class="admin-card">
        <div style="overflow-x:auto;">
          <table class="admin-table" id="pricing-table">
            <thead>
              <tr>
                <th>City</th>
                <th>Category</th>
                <th>Item</th>
                <th>Unit</th>
                <th>Rate (₹)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody id="pricing-tbody">
              <tr><td colspan="7" style="text-align:center;padding:var(--space-8);">Loading rates...</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <div class="modal-overlay" id="rate-modal" style="display:none;">
        <div class="modal-dialog" style="max-width:500px;">
          <div class="modal-header">
            <h3 id="rate-modal-title">Add Rate</h3>
            <button class="modal-close" id="rate-modal-close">&times;</button>
          </div>
          <form id="rate-form" class="modal-body" style="display:flex;flex-direction:column;gap:var(--space-4);">
            <input type="hidden" id="rate-id" />
            <div class="form-group">
              <label class="form-label">City</label>
              <input type="text" id="rate-city" class="form-input" required placeholder="e.g. Chennai" />
            </div>
            <div class="form-group">
              <label class="form-label">Category</label>
              <select id="rate-category" class="form-input" required>
                <option value="modular_kitchen">Modular Kitchen</option>
                <option value="wardrobe">Wardrobe</option>
                <option value="flooring">Flooring</option>
                <option value="painting">Painting</option>
                <option value="false_ceiling">False Ceiling</option>
                <option value="electrical">Electrical</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Item Name</label>
              <input type="text" id="rate-item" class="form-input" required placeholder="e.g. Base Cabinets" />
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-4);">
              <div class="form-group">
                <label class="form-label">Unit</label>
                <input type="text" id="rate-unit" class="form-input" required placeholder="e.g. sq.ft" />
              </div>
              <div class="form-group">
                <label class="form-label">Rate per Unit (₹)</label>
                <input type="number" id="rate-price" class="form-input" required step="0.01" min="0" />
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Description</label>
              <textarea id="rate-desc" class="form-input" rows="2"></textarea>
            </div>
            <button type="submit" class="btn btn-primary" style="width:100%;">Save Rate</button>
          </form>
        </div>
      </div>
    `;

  return renderAdminLayout('pricing', 'Pricing Engine', content);
}

export function initPricingAdmin() {
  initAdminLayout();
  let rates = [];

  async function loadRates() {
    try {
      const data = await apiRequest('/pricing');
      rates = Array.isArray(data) ? data : (data.data || []);
      renderTable();
    } catch (e) {
      const tbody = document.getElementById('pricing-tbody');
      if (tbody) tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;color:var(--color-error);">Failed to load rates</td></tr>';
    }
  }

  function renderTable() {
    const city = document.getElementById('city-filter')?.value;
    const cat = document.getElementById('category-filter')?.value;
    let filtered = rates;
    if (city) filtered = filtered.filter(r => r.city === city);
    if (cat) filtered = filtered.filter(r => r.category === cat);

    const tbody = document.getElementById('pricing-tbody');
    if (!tbody) return;

    if (!filtered.length) {
      tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:var(--space-6);">No rates found</td></tr>';
      return;
    }

    tbody.innerHTML = filtered.map(r => `
      <tr>
        <td><strong>${r.city}</strong></td>
        <td><span class="badge badge-info">${(r.category || '').replace(/_/g, ' ')}</span></td>
        <td>${r.item_name}</td>
        <td>${r.unit}</td>
        <td style="font-weight:600;">₹${parseFloat(r.rate_per_unit).toLocaleString('en-IN')}</td>
        <td><span class="badge ${r.is_active == 1 ? 'badge-success' : 'badge-secondary'}">${r.is_active == 1 ? 'Active' : 'Inactive'}</span></td>
        <td>
          <button class="btn btn-sm btn-outline edit-rate" data-id="${r.id}">Edit</button>
          <button class="btn btn-sm btn-danger delete-rate" data-id="${r.id}" style="margin-left:4px;">Delete</button>
        </td>
      </tr>
    `).join('');

    tbody.querySelectorAll('.edit-rate').forEach(btn => {
      btn.addEventListener('click', () => {
        const rate = rates.find(r => r.id == btn.dataset.id);
        if (rate) openModal(rate);
      });
    });

    tbody.querySelectorAll('.delete-rate').forEach(btn => {
      btn.addEventListener('click', async () => {
        if (confirm('Delete this rate?')) {
          try {
            await apiRequest(`/pricing/${btn.dataset.id}`, { method: 'DELETE' });
            loadRates();
          } catch (e) { alert('Delete failed'); }
        }
      });
    });
  }

  function openModal(rate = null) {
    document.getElementById('rate-modal').style.display = 'flex';
    document.getElementById('rate-modal-title').textContent = rate ? 'Edit Rate' : 'Add Rate';
    document.getElementById('rate-id').value = rate ? rate.id : '';
    document.getElementById('rate-city').value = rate ? rate.city : '';
    document.getElementById('rate-category').value = rate ? rate.category : 'modular_kitchen';
    document.getElementById('rate-item').value = rate ? rate.item_name : '';
    document.getElementById('rate-unit').value = rate ? rate.unit : '';
    document.getElementById('rate-price').value = rate ? rate.rate_per_unit : '';
    document.getElementById('rate-desc').value = rate ? (rate.description || '') : '';
  }

  document.getElementById('add-rate-btn')?.addEventListener('click', () => openModal());
  document.getElementById('rate-modal-close')?.addEventListener('click', () => {
    document.getElementById('rate-modal').style.display = 'none';
  });

  document.getElementById('rate-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = document.getElementById('rate-id').value;
    const body = {
      city: document.getElementById('rate-city').value,
      category: document.getElementById('rate-category').value,
      item_name: document.getElementById('rate-item').value,
      unit: document.getElementById('rate-unit').value,
      rate_per_unit: parseFloat(document.getElementById('rate-price').value),
      description: document.getElementById('rate-desc').value
    };

    try {
      if (id) {
        await apiRequest(`/pricing/${id}`, { method: 'PUT', body });
      } else {
        await apiRequest('/pricing', { method: 'POST', body });
      }
      document.getElementById('rate-modal').style.display = 'none';
      loadRates();
    } catch (e) { alert('Save failed: ' + e.message); }
  });

  document.getElementById('city-filter')?.addEventListener('change', renderTable);
  document.getElementById('category-filter')?.addEventListener('change', renderTable);

  loadRates();
}
